import React, { useEffect, useMemo } from "react";
import {
  WEEKDAY_KEYS,
  WEEKDAY_LABELS,
  timeToInputValue,
  inputValueToApiTime,
  validateWeeklyHoursObject,
} from "../../../../utils/barWeeklyHours";

/**
 * Opening hours per weekday (venue hours). Separate from show "time slots".
 *
 * @param {object} props
 * @param {object} props.value — weekly hours keyed mon..sun
 * @param {(next: object) => void} props.onChange
 * @param {boolean} [props.disabled]
 * @param {(err: string) => void} [props.onValidationChange]
 */
const BarWeeklyHoursSection = ({
  value,
  onChange,
  disabled = false,
  onValidationChange,
}) => {
  const errorText = useMemo(() => validateWeeklyHoursObject(value), [value]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(errorText);
    }
  }, [errorText, onValidationChange]);

  const setDay = (key, patch) => {
    onChange({
      ...value,
      [key]: { ...value[key], ...patch },
    });
  };

  const handleClosedToggle = (key, closed) => {
    if (closed) {
      setDay(key, { closed: true });
      return;
    }
    const fallback = value.mon?.closed === false ? value.mon : value.tue;
    const open = fallback?.open || "11:00:00";
    const close = fallback?.close || "02:00:00";
    setDay(key, {
      closed: false,
      open,
      close,
    });
  };

  const copyMondayToAll = () => {
    const mon = value.mon;
    if (!mon || mon.closed) return;
    const next = { ...value };
    for (const k of WEEKDAY_KEYS) {
      next[k] = {
        closed: false,
        open: mon.open,
        close: mon.close,
      };
    }
    onChange(next);
  };

  return (
    <div className="bar-weekly-hours-section">
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "0.75rem",
          marginBottom: "0.75rem",
        }}
      >
        <h3 className="section-subtitle" style={{ margin: 0 }}>
          Opening hours *
        </h3>
        <button
          type="button"
          className="modal-btn secondary"
          style={{ fontSize: "0.85rem", padding: "0.35rem 0.65rem" }}
          onClick={copyMondayToAll}
          disabled={disabled || !value?.mon || value.mon.closed}
        >
          Copy Monday to all days
        </button>
      </div>
      <p className="input-hint" style={{ marginTop: 0 }}>
        Mark days closed or set open and close (overnight hours are OK, e.g.
        12:00 to 01:00).
      </p>
      <div
        className="bar-weekly-hours-table"
        style={{ overflowX: "auto" }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "0.95rem",
          }}
        >
          <thead>
            <tr>
              <th
                style={{ textAlign: "left", padding: "0.35rem 0.5rem" }}
              >
                Day
              </th>
              <th style={{ padding: "0.35rem 0.5rem" }}>Closed</th>
              <th style={{ padding: "0.35rem 0.5rem" }}>Opens</th>
              <th style={{ padding: "0.35rem 0.5rem" }}>Closes</th>
            </tr>
          </thead>
          <tbody>
            {WEEKDAY_KEYS.map((key) => {
              const d = value[key];
              const closed = d?.closed === true;
              const openVal = timeToInputValue(d?.open);
              const closeVal = timeToInputValue(d?.close);
              return (
                <tr key={key}>
                  <td style={{ padding: "0.35rem 0.5rem", fontWeight: 500 }}>
                    {WEEKDAY_LABELS[key]}
                  </td>
                  <td style={{ padding: "0.35rem 0.5rem", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={closed}
                      onChange={(e) => handleClosedToggle(key, e.target.checked)}
                      disabled={disabled}
                      aria-label={`${WEEKDAY_LABELS[key]} closed`}
                    />
                  </td>
                  <td style={{ padding: "0.35rem 0.5rem" }}>
                    <input
                      type="time"
                      className="settings-input"
                      value={openVal}
                      onChange={(e) =>
                        setDay(key, {
                          closed: false,
                          open: inputValueToApiTime(e.target.value),
                        })
                      }
                      disabled={disabled || closed}
                      aria-label={`${WEEKDAY_LABELS[key]} opens`}
                    />
                  </td>
                  <td style={{ padding: "0.35rem 0.5rem" }}>
                    <input
                      type="time"
                      className="settings-input"
                      value={closeVal}
                      onChange={(e) =>
                        setDay(key, {
                          closed: false,
                          close: inputValueToApiTime(e.target.value),
                        })
                      }
                      disabled={disabled || closed}
                      aria-label={`${WEEKDAY_LABELS[key]} closes`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {errorText ? (
        <div
          style={{
            color: "var(--primary-red)",
            fontSize: "0.85rem",
            marginTop: "0.5rem",
          }}
          role="alert"
        >
          {errorText}
        </div>
      ) : null}
    </div>
  );
};

export default BarWeeklyHoursSection;
